/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useRef, useState } from "react";
import { toOutletDisplayName } from "@zenith/features/OutletManagement/utils/outlet-display-name";
import {
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import Check from "@zenith/assets/icons/Check";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import ChevronRight from "@zenith/assets/icons/ChevronRight";
import {
  DoubleArrowSortIcon,
  InflowIcon,
  OutflowIcon,
} from "@zenith/assets/icons/components";
import {
  ButtonComponent,
  NestedMenuItemComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import {
  useNotificationContext,
  useTransactionsContext,
} from "@zenith/context";
import {
  NOT_APPLICABLE_TEXT,
  TRANSACTIONS_DATE_FILTER_FORMAT,
  TRANSACTIONS_DATE_FORMAT,
  TRANSACTIONS_DESCRIPTION_LENGTH,
  TRANSACTIONS_EXPORT_FILENAME,
  TRANSACTIONS_PAGE_SIZE,
  TRANSACTION_NOT_FOUND_ERROR_CODE,
} from "@zenith/data/constants";
import { useTablePagination } from "@zenith/hooks";
import { useLoadOutlets } from "@zenith/features/OutletManagement/hooks/useLoadOutlets";
import { useOutletView } from "@zenith/features/OutletManagement/hooks/useOutletView";
import { getPaymentMethods } from "@zenith/features/Transactions/utils/getPaymentMethods";
import { TRANSACTIONS_TRANSACTION_DETAILS } from "@zenith/routes/paths";
import {
  ApiResponseDTOPaginationRecordListTransactionBaseDTO,
  TransactionBaseDTO,
  TransactionsApiService,
} from "@zenith/services/transactions";
import { formatCurrency } from "@zenith/utils";
import { downloadFileFromBlob } from "@zenith/utils/Helpers/downloadFileUtil";
import { useNavigate } from "react-router-dom";
import AmountTransactionFilter from "../../TransactionFilters/amount-transaction-filter/amount-transactions-filter-component";
import DateTransactionsFilter from "../../TransactionFilters/date-transaction-filter/date-transactions-filter/date-transactions-filter";
import {
  TransactionsParametersExportRecord,
  TransactionsParametersRecord,
} from "@zenith/features/Transactions/interfaces";
import { EmptyState } from "@zenith/components/ui/EmptyStateComponent/empty-state-component/empty-state-component";
import { NoTransaction } from "@zenith/assets/illustrations/NoTransaction";
import { SearchComponent } from "@zenith/components/ui/SearchComponent/search-component";
import ServerErrorHandler from "@zenith/features/AppStateHandlers/ServerErrorHandler/server-error-handler";
import { mapPaymentMethodWithType } from "@zenith/utils/ExtensionMethods";
import { removeUnderscores } from "@zenith/utils/Formaters/removeUnderscore";
import {
  OutletApiService,
  OutletPosDTO,
  TeamManagementControllerService,
  UserManagementDTO,
} from "@zenith/services/user";

type PaymentMethodsType =
  | "USSD"
  | "QR_CODE"
  | "IN_BANK_TRANSFER"
  | "INWARDS_TRANSFER"
  | "PAY_WITH_TRANSFER"
  | "PAYMENT_LINK_PAY_WITH_TRANSFER"
  | "MOMO"
  | "OUT_BANK_TRANSFER"
  | "REFUND"
  | "PAYMENT_LINK_CARD"
  | "PAYMENT_LINK_USSD"
  | "PAYMENT_LINK_QR_CODE"
  | "PAYMENT_LINK_BANK_TRANSFER"
  | "POS"
  | "POS_ORDER"
  | "STAMP_DUTY"
  | "TRANSACTION_CHARGE";

interface FiltersData {
  maximumAmount: number | undefined;
  minimumAmount: number | undefined;
  /** The outlets to show; unset follows the outlet view. */
  outletIds?: string[];
  paymentMethods: TransactionsParametersRecord["paymentMethods"];
  transactionDateEnd: string | undefined;
  transactionDateStart: string | undefined;
  terminalId?: string;
  userId?: string;
  transactionStatus?: string;
}

interface FilterOption {
  value: string;
  label: string;
}

const STATUS_OPTIONS: FilterOption[] = Object.values(
  TransactionBaseDTO.status
).map((status) => ({ value: status, label: removeUnderscores(status) }));

interface DatesFilter {
  startDate: unknown;
  endDate: unknown;
}

interface TransactionsTableComponentProps {
  filters: FiltersData;
  setFilters: React.Dispatch<React.SetStateAction<FiltersData>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  direction: boolean;
  setDirection: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Get the display name for transaction type using the existing mapPaymentMethodWithType
 * This handles fee transactions and delegates to mapPaymentMethod for the rest
 */
function getTransactionTypeDisplay(transaction: TransactionBaseDTO): string {
  return mapPaymentMethodWithType(
    transaction.paymentMethod,
    transaction.partialTransactionType
  );
}

/**
 * Names outlets the way a sentence reads: "A", "A and B", "A, B and C".
 */
function formatOutletNames(names: string[]): string {
  const display = names.map((name) => toOutletDisplayName(name) ?? name);

  if (display.length <= 1) return display[0] ?? "";

  return `${display.slice(0, -1).join(", ")} and ${
    display[display.length - 1]
  }`;
}

const TransactionsTableComponent = memo(function TransactionsTableComponent({
  filters,
  setFilters,
  searchText,
  setSearchText,
  currentPage,
  setCurrentPage,
  direction,
  setDirection,
}: TransactionsTableComponentProps & {
  filters: FiltersData;
  setFilters: React.Dispatch<React.SetStateAction<FiltersData>>;
}) {
  const {
    transactionsContext,
    setTransactionsContext,
    clearTransactionsContext,
  } = useTransactionsContext();
  const { hasTransactions, isExport, mediaType, totalNoOfTransactions } =
    transactionsContext;
  const { setOpen } = useNotificationContext();
  const {
    outlets,
    outletIdsForRequests,
    isLoadingOutlets,
    isAllOutletsSelected,
    selectedOutlets,
  } = useOutletView();
  const outletIdsKey = outletIdsForRequests.join(",");
  const previousOutletIdsKey = useRef<string | null>(null);
  const loadOutlets = useLoadOutlets();
  // The outlets the caller may act on, already loaded for the outlet view.
  const permittedOutlets = outlets.filter((outlet) => outlet.id);

  const showOutletFilter = isAllOutletsSelected && permittedOutlets.length > 1;
  const requestOutletIds = isAllOutletsSelected
    ? filters.outletIds ?? []
    : outletIdsForRequests;
  const viewOutletIds = selectedOutlets
    .map((outlet) => outlet.id)
    .filter((id): id is string => Boolean(id));
  const filterOutletNames = (
    isAllOutletsSelected ? filters.outletIds ?? [] : viewOutletIds
  )
    .map(
      (id) =>
        permittedOutlets.find((outlet) => outlet.id === id)?.name ??
        selectedOutlets.find((outlet) => outlet.id === id)?.name
    )
    .filter((name): name is string => Boolean(name));

  const terminalOutletIds = isAllOutletsSelected
    ? permittedOutlets
        .map((outlet) => outlet.id)
        .filter((id): id is string => Boolean(id))
    : viewOutletIds;

  const [terminalOptions, setTerminalOptions] = useState<FilterOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<FilterOption[]>([]);
  const terminalOutletIdsKey = terminalOutletIds.join(",");
  const requestOutletIdsKey = requestOutletIds.join(",");

  useEffect(() => {
    if (!terminalOutletIds.length) {
      setTerminalOptions([]);
      return;
    }

    let isCurrent = true;
    const unique = (options: FilterOption[]) =>
      options.filter(
        (option, index) =>
          options.findIndex((other) => other.value === option.value) === index
      );

    Promise.all(
      terminalOutletIds.map((outletId) =>
        OutletApiService.getOutletPosTerminals(outletId)
          .then((response) => response.data ?? [])
          .catch((error): OutletPosDTO[] => {
            console.error("Error fetching outlet terminals:", error);
            return [];
          })
      )
    ).then((terminalsPerOutlet) => {
      if (!isCurrent) return;
      setTerminalOptions(
        unique(
          terminalsPerOutlet
            .flat()
            .filter((pos) => pos.terminalId)
            .map((pos) => ({
              value: pos.terminalId as string,
              label: pos.terminalId as string,
            }))
        )
      );
    });

    return () => {
      isCurrent = false;
    };
  }, [terminalOutletIdsKey]);

  useEffect(() => {
    const TEAM_PAGE_SIZE = 100;
    let isCurrent = true;

    const loadOutletTeam = async (outletId?: string) => {
      const team: UserManagementDTO[] = [];
      try {
        for (let page = 0; ; page++) {
          const response = await TeamManagementControllerService.list({
            page,
            size: TEAM_PAGE_SIZE,
            status: "ACTIVE",
            ...(outletId ? { outletId } : {}),
          });
          const members = response.data?.members ?? [];
          team.push(...members);

          const totalItems = response.data?.totalItems ?? 0;
          if (!members.length || team.length >= totalItems) break;
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
      }
      return team;
    };

    const loadTeam = async () => {
      const team = (
        await Promise.all(
          (requestOutletIds.length ? requestOutletIds : [undefined]).map(
            loadOutletTeam
          )
        )
      ).flat();

      if (!isCurrent) return;
      const options = team
        .filter(
          (member, index) =>
            member.userId &&
            team.findIndex((other) => other.userId === member.userId) === index
        )
        .map((member) => ({
          value: member.userId as string,
          label:
            [member.firstName, member.lastName].filter(Boolean).join(" ") ||
            member.email ||
            (member.userId as string),
        }))
        .sort((first, second) => first.label.localeCompare(second.label));

      setEmployeeOptions(options);
      setFilters((current) =>
        current.userId &&
        !options.some((option) => option.value === current.userId)
          ? { ...current, userId: undefined }
          : current
      );
    };

    loadTeam();

    return () => {
      isCurrent = false;
    };
  }, [requestOutletIdsKey]);

  const initialFilters: FiltersData = {
    maximumAmount: undefined,
    minimumAmount: undefined,
    paymentMethods: [],
    transactionDateEnd: undefined,
    transactionDateStart: undefined,
    terminalId: undefined,
    userId: undefined,
    transactionStatus: undefined,
  };
  const [isLoadingTransactions, setIsLoadingTransactions] =
    useState<boolean>(true);
  const [isEmptySearch, setIsEmptySearch] = useState(false);
  const [searchTextToSend, setSearchTextToSend] = useState<string>("");
  const [paymentMethodList, setPaymentMethodList] = useState<
    PaymentMethodsType[]
  >([]);
  const [transactions, setTransactions] = useState<TransactionBaseDTO[]>();
  const [isNewRequest, setIsNewRequest] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [dates, setDates] = useState<DatesFilter>({
    startDate: undefined,
    endDate: undefined,
  });
  const [serverError, setServerError] = useState<boolean>(false);

  const { maxPage } = useTablePagination(
    TRANSACTIONS_PAGE_SIZE,
    totalNoOfTransactions as number
  );

  const navigate = useNavigate();

  // #region Transactions

  function handlePageChange(p: number): void {
    setIsLoadingTransactions(true);
    setIsNewRequest(true);
    setCurrentPage(p);
  }

  function handlePrevPage(): void {
    if (currentPage > 1) {
      setIsLoadingTransactions(true);
      setIsNewRequest(true);
      setCurrentPage(currentPage - 1);
    }
  }

  function handleNextPage(): void {
    if (currentPage < maxPage) {
      setIsLoadingTransactions(true);
      setIsNewRequest(true);
      setCurrentPage(currentPage + 1);
    }
  }

  // #endregion Transactions

  // #region Search

  useEffect(() => {
    if (!searchText && isEmptySearch && filters === initialFilters) {
      setIsEmptySearch(false);
    }
  }, [isEmptySearch, searchText, filters]);

  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    if (debouncedSearchText === "" && searchTextToSend !== "") {
      setSearchTextToSend("");
      setIsNewRequest(true);
      handlePageChange(1);
    }
  }, [debouncedSearchText, searchTextToSend]);

  useEffect(() => {
    if (debouncedSearchText) {
      setSearchTextToSend(debouncedSearchText);
      setIsNewRequest(true);
      handlePageChange(1);
    }
  }, [debouncedSearchText]);

  const clearSearch = () => {
    setSearchText("");
    setSearchTextToSend("");
    setIsNewRequest(true);
    handlePageChange(1);
  };

  // #endregion Search

  // #region Filters

  const handleFiltersButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (!open) {
      setAnchorEl(e.currentTarget);
    } else {
      setAnchorEl(null);
    }
  };

  function handleMenuClose(): void {
    return setAnchorEl(null);
  }

  function clearFilters(): void {
    setDates({ startDate: undefined, endDate: undefined });
    setFilters(initialFilters);
    setIsNewRequest(true);
  }

  function handleClearAllButtonClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    clearFilters();
    handleMenuClose();
  }

  function handleApplyButtonClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    handlePageChange(1);
    setIsNewRequest(true);
    handleMenuClose();
  }

  function handlePaymentMethodClick(paymentMethod: PaymentMethodsType) {
    const currentMethods = filters.paymentMethods || [];
    if (currentMethods.includes(paymentMethod)) {
      setFilters({
        ...filters,
        paymentMethods: currentMethods.filter((pm) => pm !== paymentMethod),
      });
    } else {
      setFilters({
        ...filters,
        paymentMethods: [...currentMethods, paymentMethod],
      });
    }
  }

  function handleOutletFilterClick(outletId: string) {
    const selected = filters.outletIds || [];
    setFilters({
      ...filters,
      outletIds: selected.includes(outletId)
        ? selected.filter((id) => id !== outletId)
        : [...selected, outletId],
    });
  }

  function handleSingleSelectFilterClick(
    key: "terminalId" | "userId",
    value: string
  ) {
    setFilters({
      ...filters,
      [key]: filters[key] === value ? undefined : value,
    });
  }

  function handleStatusClick(status: string) {
    setFilters({
      ...filters,
      transactionStatus:
        filters.transactionStatus === status ? undefined : status,
    });
  }

  function filtersLength(): number {
    let length = 0;

    if (filters.paymentMethods?.length)
      length += filters.paymentMethods?.length;

    if (filters.maximumAmount || filters.minimumAmount) length++;

    if (filters.transactionDateStart || filters.transactionDateEnd) length++;

    if (showOutletFilter) length += filters.outletIds?.length || 0;

    if (filters.terminalId) length++;

    if (filters.userId) length++;

    if (filters.transactionStatus) length++;

    return length;
  }

  const filterCount = filtersLength();

  useEffect(() => {
    getPaymentMethods()
      .then((response) => {
        if ("data" in response && response.data) {
          const filteredPaymentMethods = response.data.filter(
            (pm) => (pm as string) !== "IN_BANK_TRANSFER"
          ) as PaymentMethodsType[];
          setPaymentMethodList(filteredPaymentMethods);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }, []);

  // #endregion Filters

  const getRequestParams = () => {
    const {
      maximumAmount,
      minimumAmount,
      paymentMethods,
      transactionDateEnd,
      transactionDateStart,
      terminalId,
      userId,
      transactionStatus,
    } = filters || {};

    const page = currentPage <= 0 ? 0 : currentPage - 1;

    return {
      page: page.toString(),
      size: TRANSACTIONS_PAGE_SIZE.toString(),
      direction: direction
        ? TransactionsParametersRecord.direction.ASC
        : TransactionsParametersRecord.direction.DESC,
      transactionDateStart,
      transactionDateEnd,
      paymentMethods: paymentMethods?.join(","),
      minimumAmount: minimumAmount?.toString(),
      maximumAmount: maximumAmount?.toString(),
      userId,
      terminalId,
      outletId: requestOutletIds.length
        ? requestOutletIds.join(",")
        : undefined,
      status: transactionStatus,
    };
  };

  function handleTransactionDetails(
    transactionId?: string,
    mappedPaymentMethod?: string
  ) {
    navigate(TRANSACTIONS_TRANSACTION_DETAILS, {
      state: {
        id: transactionId,
        isOperation: false,
        ...(mappedPaymentMethod && { mappedPaymentMethod }),
        filters,
        searchText,
        currentPage,
        direction,
      },
    });
  }

  function clearAmountFilter(): void {
    setFilters({
      ...filters,
      minimumAmount: undefined,
      maximumAmount: undefined,
    });
    setIsNewRequest(true);
  }

  function applyAmountFilter(
    minimumAmount: number | undefined,
    maximumAmount: number | undefined
  ): void {
    setFilters({
      ...filters,
      minimumAmount,
      maximumAmount,
    });
  }

  function clearDateFilter(): void {
    setDates({ startDate: undefined, endDate: undefined });
    setFilters({
      ...filters,
      transactionDateStart: undefined,
      transactionDateEnd: undefined,
    });
    setIsNewRequest(true);
  }

  function applyDateFilter(
    transactionDateStart: unknown | undefined,
    transactionDateEnd: unknown | undefined
  ): void {
    setDates({ startDate: transactionDateStart, endDate: transactionDateEnd });
    formatDateToFilters(transactionDateStart, transactionDateEnd);
  }

  function formatDateToFilters(
    transactionDateStart: unknown,
    transactionDateEnd: unknown
  ) {
    let startDate: string | undefined = undefined;
    let endDate: string | undefined = undefined;

    if (
      transactionDateStart &&
      typeof transactionDateStart === "object" &&
      "$d" in transactionDateStart
    ) {
      startDate = dayjs(transactionDateStart.$d as string).format(
        TRANSACTIONS_DATE_FILTER_FORMAT
      );
    }

    if (
      transactionDateEnd &&
      typeof transactionDateEnd === "object" &&
      "$d" in transactionDateEnd
    ) {
      endDate = dayjs(transactionDateEnd.$d as string).format(
        TRANSACTIONS_DATE_FILTER_FORMAT
      );
    }

    setFilters({
      ...filters,
      transactionDateStart: startDate,
      transactionDateEnd: endDate,
    });
  }

  function descriptionLimit(description: string | undefined) {
    if (description && description.length >= TRANSACTIONS_DESCRIPTION_LENGTH) {
      return description.slice(0, TRANSACTIONS_DESCRIPTION_LENGTH) + "…";
    } else {
      return description;
    }
  }

  useEffect(() => {
    if (isNewRequest && !isLoadingOutlets) {
      setIsLoadingTransactions(true);
      const params = getRequestParams();

      TransactionsApiService.getTransactions(
        params.page,
        params.size,
        params.direction,
        undefined,
        params.transactionDateStart,
        params.transactionDateEnd,
        undefined,
        params.paymentMethods,
        undefined,
        params.minimumAmount,
        params.maximumAmount,
        searchText || undefined,
        params.userId,
        params.terminalId,
        params.outletId,
        params.status
      )
        .then(
          (response: ApiResponseDTOPaginationRecordListTransactionBaseDTO) => {
            if ("data" in response && response.data) {
              setTransactions(response.data?.content);
              setIsLoadingTransactions(false);
              setTransactionsContext({
                ...transactionsContext,
                hasTransactions:
                  (response?.data?.content?.length as number) > 0,
                totalNoOfTransactions: response.data?.totalItems as number,
                isOperation: false,
              });
              setIsEmptySearch(false);
              setIsNewRequest(false);
            }
          }
        )
        .catch((error) => {
          setIsLoadingTransactions(false);
          if (error?.status === 403) {
            loadOutlets().catch((reloadError) =>
              console.error("Failed to reload outlets:", reloadError)
            );
            if (filters.outletIds?.length) {
              setFilters((current) => ({ ...current, outletIds: undefined }));
              setIsNewRequest(true);
            }
            return;
          }
          if (error.body.internalCode === TRANSACTION_NOT_FOUND_ERROR_CODE) {
            setTransactionsContext({
              ...transactionsContext,
              hasTransactions: false,
              totalNoOfTransactions: 0,
              isOperation: false,
            });
            setTransactions([]);
            (searchText || filters !== initialFilters) &&
              setIsEmptySearch(true);
            setIsNewRequest(false);
          } else if (typeof error.status === "number" && error.status >= 500) {
            setServerError(true);
          }

          throw new Error(error);
        });
    }
  }, [direction, currentPage, debouncedSearchText, isNewRequest, isLoadingOutlets]);

  useEffect(() => {
    if (previousOutletIdsKey.current === null) {
      previousOutletIdsKey.current = outletIdsKey;
      return;
    }

    if (previousOutletIdsKey.current === outletIdsKey) return;

    previousOutletIdsKey.current = outletIdsKey;
    setCurrentPage(1);
    setFilters((current) => ({
      ...current,
      outletIds: undefined,
      terminalId: undefined,
      userId: undefined,
    }));
    setIsNewRequest(true);
  }, [outletIdsKey]);

  useEffect(() => {
    if (isExport) {
      const params = getRequestParams();

      TransactionsApiService.export1(
        mediaType,
        params.page,
        params.size,
        params.direction,
        undefined,
        params.transactionDateStart,
        params.transactionDateEnd,
        undefined,
        params.paymentMethods,
        undefined,
        params.minimumAmount,
        params.maximumAmount,
        undefined,
        params.userId,
        params.terminalId,
        undefined,
        params.outletId,
        params.status
      )
        .then((response: Blob) => {
          downloadFileFromBlob(
            response,
            TRANSACTIONS_EXPORT_FILENAME +
              "-" +
              new Date().getTime().toString(),
            mediaType as TransactionsParametersExportRecord.mediaType
          );
          setOpen(true);
        })
        .catch((error: unknown) => {
          throw new Error(String(error));
        })
        .finally(() => {
          setTransactionsContext({ ...transactionsContext, isExport: false });
        });
    }
  }, [isExport]);

  const nestedMenuItemClassNames =
    "w-[213px] ml-2.5 py-4 px-2 w-52 hover:rounded-lg hover:bg-[#F9F9F9] text-zinc-600";

  const isSmallScreen = useMediaQuery("(max-width: 640px)");

  return (
    <>
      <ToastSnackbarComponent
        horizontal="right"
        status={"success"}
        vertical="bottom"
        className="mt-36 ml-40"
        size="sm"
        title={"Export Completed"}
        text={`Your ${transactionsContext.mediaType} was successfully exported.`}
        hasAutoHideDuration
      />

      <div className="mt-8 mb-5 block flex-wrap md:flex">
        <SearchComponent
          size="medium"
          placeholder="Search by transaction ID"
          value={searchText}
          onChange={setSearchText}
          onClear={clearSearch}
          isAutocomplete={false}
          className="mr-4 w-[325px]"
        />

        <button
          className={`mt-2 inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-center text-sm font-normal capitalize leading-tight text-zinc-600 md:mt-0 ${
            !open
              ? `max-w-103 border border-stone-300 hover:border-neutral-300`
              : `w-108 bg-zinc-100 hover:border-neutral-300`
          }`}
          id="filters-button"
          onClick={handleFiltersButtonClick}
        >
          {!open ? "Filters" : "Close Filters"}{" "}
          {!open && filterCount > 0 && (
            <Chip
              className="inline-flex h-6 w-6 flex-col items-center justify-center gap-2.5 rounded-3xl bg-neutral-400 px-1.5 text-center text-xs font-medium text-white"
              label={filterCount}
              size="small"
            />
          )}
        </button>

        {!open && filterCount > 0 && (
          <ButtonComponent
            label="Clear all filters"
            onClick={handleClearAllButtonClick}
            id="clear-all-button"
            size="large"
            type="tertiary"
          />
        )}

        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          open={open}
          onClose={() => setAnchorEl(null)}
          sx={{
            ".MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPopover-paper.MuiMenu-paper.MuiMenu-paper":
              {
                display: "flex",
                width: "229px",
                flexDirection: "column",
                alignItems: "flex-start",
              },
          }}
        >
          {showOutletFilter && (
            <NestedMenuItemComponent
              label="Outlet"
              parentMenuOpen={open}
              className={nestedMenuItemClassNames}
              sx={{
                ".MuiTypography-root": {
                  fontSize: "14px",
                },
              }}
              filterCount={filters.outletIds?.length}
            >
              {permittedOutlets.map((outlet) => (
                <MenuItem
                  className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal leading-none text-zinc-600 hover:bg-stone-50"
                  id={`outlet-${outlet.id}-filter-button`}
                  onClick={() => handleOutletFilterClick(outlet.id as string)}
                  key={outlet.id}
                >
                  {toOutletDisplayName(outlet.name) ?? outlet.name}
                  {filters.outletIds?.includes(outlet.id as string) && (
                    <Check />
                  )}
                </MenuItem>
              ))}
            </NestedMenuItemComponent>
          )}

          <NestedMenuItemComponent
            label="Transaction Type"
            parentMenuOpen={open}
            className={nestedMenuItemClassNames}
            sx={{
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
            filterCount={filters.paymentMethods?.length}
          >
            {paymentMethodList?.map((pm: PaymentMethodsType) => (
              <MenuItem
                className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal leading-none text-zinc-600 hover:bg-stone-50"
                id={`${pm}-filter-button`}
                onClick={() => handlePaymentMethodClick(pm)}
                key={pm}
              >
                {pm?.mapPaymentMethod ? pm.mapPaymentMethod() : pm}
                {filters?.paymentMethods?.includes(pm as any) && <Check />}
              </MenuItem>
            ))}
          </NestedMenuItemComponent>

          <NestedMenuItemComponent
            label="Date"
            parentMenuOpen={open}
            className={nestedMenuItemClassNames}
            sx={{
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
            filterCount={
              filters.transactionDateStart || filters.transactionDateEnd ? 1 : 0
            }
          >
            <DateTransactionsFilter
              minDate={dates.startDate}
              maxDate={dates.endDate}
              clear={clearDateFilter}
              apply={applyDateFilter}
            />
          </NestedMenuItemComponent>

          <NestedMenuItemComponent
            label="Amount"
            parentMenuOpen={open}
            className={nestedMenuItemClassNames}
            sx={{
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
            filterCount={filters.maximumAmount || filters.minimumAmount ? 1 : 0}
          >
            <AmountTransactionFilter
              maximumValue={filters.maximumAmount}
              minimumValue={filters.minimumAmount}
              clear={clearAmountFilter}
              apply={applyAmountFilter}
            />
          </NestedMenuItemComponent>

          {[
            {
              key: "terminalId" as const,
              label: "Terminal ID",
              options: terminalOptions,
            },
            {
              key: "userId" as const,
              label: "Employee",
              options: employeeOptions,
            },
          ]
            .filter(({ options }) => options.length > 0)
            .map(({ key, label, options }) => (
              <NestedMenuItemComponent
                key={key}
                label={label}
                parentMenuOpen={open}
                className={nestedMenuItemClassNames}
                sx={{
                  ".MuiTypography-root": {
                    fontSize: "14px",
                  },
                }}
                filterCount={filters[key] ? 1 : 0}
              >
                {options.map((option) => (
                  <MenuItem
                    className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal leading-none text-zinc-600 hover:bg-stone-50"
                    id={`${key}-${option.value}-filter-button`}
                    onClick={() =>
                      handleSingleSelectFilterClick(key, option.value)
                    }
                    key={option.value}
                  >
                    {option.label}
                    {filters[key] === option.value && <Check />}
                  </MenuItem>
                ))}
              </NestedMenuItemComponent>
            ))}

          <NestedMenuItemComponent
            label="Status"
            parentMenuOpen={open}
            className={nestedMenuItemClassNames}
            sx={{
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
            filterCount={filters.transactionStatus ? 1 : 0}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem
                className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal leading-none text-zinc-600 hover:bg-stone-50"
                id={`status-${option.value}-filter-button`}
                onClick={() => handleStatusClick(option.value)}
                key={option.value}
              >
                {option.label}
                {filters.transactionStatus === option.value && <Check />}
              </MenuItem>
            ))}
          </NestedMenuItemComponent>

          <MenuItem className="mt-3 flex w-full cursor-default flex-wrap justify-between gap-1 hover:bg-white">
            <ButtonComponent
              label="Clear all"
              onClick={handleClearAllButtonClick}
              id="clear-all-button"
              size="large"
              type="tertiary"
              disabled={!filterCount}
            />
            <ButtonComponent
              label={`${`Apply ${filterCount > 0 ? `(${filterCount})` : ""}`}`}
              onClick={handleApplyButtonClick}
              id="apply-button"
              size="medium"
              type="primary"
              disabled={!filterCount}
            />
          </MenuItem>
        </Menu>
      </div>

      <p className="my-4 text-sm text-neutral-600 md:my-8">
        {`Showing ${(!isLoadingTransactions && transactions?.length) || 0} of ${
          (!isLoadingTransactions && totalNoOfTransactions) || 0
        } Transactions`}
      </p>
      <div className="overflow-auto border-t border-neutral-200">
        <Table stickyHeader>
          <TableHead>
            {(totalNoOfTransactions as number) > 0 && (
              <TableRow className="[&>th]:border-b-0 [&>th]:pt-6">
                <TableCell className="w-[5%] min-w-[40px] max-w-[60px] text-sm text-neutral-400">
                  {/* Icon column - minimal width */}
                </TableCell>
                <TableCell className="w-[15%] min-w-[120px] text-left text-sm text-neutral-400">
                  <div className="flex items-center">
                    <span className="mr-2">Date</span>
                    <button
                      className="cursor-pointer"
                      onClick={() => {
                        setDirection(!direction);
                        setIsNewRequest(true);
                      }}
                      disabled={!hasTransactions}
                    >
                      <DoubleArrowSortIcon />
                    </button>
                  </div>
                </TableCell>
                <TableCell className="w-[35%] min-w-[200px] text-left text-sm text-neutral-400">
                  Type / Description
                </TableCell>
                <TableCell className="w-[25%] min-w-[180px] text-left text-sm text-neutral-400">
                  Transaction ID
                </TableCell>
                <TableCell className="w-[20%] min-w-[120px] text-right text-sm text-neutral-400">
                  Amount / Balance
                </TableCell>
              </TableRow>
            )}
          </TableHead>

          {hasTransactions && (
            <TableBody>
              {isLoadingTransactions ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-[calc(100vh-600px)] w-full border-b-0"
                  >
                    <div className="flex justify-center text-center">
                      <CircularProgress
                        className={"text-[#F3B8B3]"}
                        size={"3rem"}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                transactions?.map((transaction: TransactionBaseDTO) => (
                  <TableRow
                    onClick={() =>
                      transaction.transactionId &&
                      handleTransactionDetails(
                        transaction.transactionId,
                        getTransactionTypeDisplay(transaction)
                      )
                    }
                    key={`${transaction.paymentMethod}-${transaction.transactionId}`}
                    className="h-16 cursor-pointer hover:bg-stone-50 [&>td]:py-2"
                  >
                    <TableCell className="w-[5%] min-w-[40px] max-w-[60px]">
                      {transaction.transactionType ===
                      TransactionBaseDTO.transactionType.OUT ? (
                        <OutflowIcon />
                      ) : (
                        <InflowIcon />
                      )}
                    </TableCell>
                    <TableCell className="w-[15%] min-w-[120px] text-neutral-400">
                      {dayjs(transaction.createdAt).format(
                        TRANSACTIONS_DATE_FORMAT
                      )}
                    </TableCell>
                    <TableCell className="w-[35%] min-w-[200px] text-neutral-600">
                      <p>{getTransactionTypeDisplay(transaction)}</p>
                      <p className="text-sm text-neutral-400">
                        {descriptionLimit(transaction.description)}
                      </p>
                    </TableCell>
                    <TableCell className="w-[25%] min-w-[180px] text-neutral-600">
                      <p>{transaction.transactionId || NOT_APPLICABLE_TEXT}</p>
                    </TableCell>
                    <TableCell className="w-[20%] min-w-[120px] text-right">
                      {transaction.transactionType ===
                        TransactionBaseDTO.transactionType.OUT && (
                        <div className="flex flex-col items-end">
                          <p className="text-red-600">
                            - {formatCurrency(transaction.totalAmount, true)}
                          </p>
                          {transaction.currentAccountBalance != null && (
                            <p className="text-xs text-neutral-400">
                              {formatCurrency(
                                transaction.currentAccountBalance,
                                true
                              )}
                            </p>
                          )}
                        </div>
                      )}
                      {transaction.transactionType ===
                        TransactionBaseDTO.transactionType.IN && (
                        <div className="flex flex-col items-end">
                          <p className="text-green-400">
                            + {formatCurrency(transaction.totalAmount, true)}
                          </p>
                          {transaction.currentAccountBalance != null && (
                            <p className="text-xs text-neutral-400">
                              {formatCurrency(
                                transaction.currentAccountBalance,
                                true
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          )}
        </Table>

        {serverError ? (
          <ServerErrorHandler
            errorTitle="We are unable to fetch transaction history"
            errorMessage="Please try again. Apologies for the inconvenience."
          />
        ) : (
          !isLoadingTransactions &&
          !hasTransactions && (
            <div className="flex h-full w-full flex-row items-center justify-center pt-16">
              <EmptyState
                title=" No Transactions Found"
                description={
                  isEmptySearch
                    ? "No matching items found."
                    : filterOutletNames.length
                    ? `No transactions to display for ${formatOutletNames(
                        filterOutletNames
                      )}`
                    : "It seems there are no transactions to display at the moment"
                }
                sub={isEmptySearch ? " Try a different search or filter" : ""}
                illustration={
                  <div className="flex w-full justify-center">
                    <NoTransaction className="block h-28 w-100 text-primary-300" />
                  </div>
                }
              />
            </div>
          )
        )}
      </div>

      <div className="mt-2 inline-flex h-8 w-full items-center justify-end">
        <div className="flex flex-row">
          <Pagination
            count={maxPage}
            page={currentPage}
            variant="text"
            shape="rounded"
            onChange={(e, p) => handlePageChange(p)}
            hidePrevButton
            siblingCount={isSmallScreen ? 0 : 1}
            hideNextButton
            sx={{
              ".MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#DA291C",
                color: "white",
              },
              ".MuiPaginationItem-root:hover": {
                backgroundColor: "#DA291C",
                color: "white",
              },
              ".MuiPaginationItem-root.Mui-selected:hover": {
                cursor: "default",
                backgroundColor: "#DA291C",
              },
            }}
          />
          {maxPage > 1 && (
            <>
              <button
                disabled={currentPage === 1}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-md text-center text-sm text-zinc-600 hover:bg-[#E67068] hover:text-white disabled:text-neutral-400 disabled:hover:bg-transparent"
                }
              >
                <ChevronLeft
                  onClick={handlePrevPage}
                  color={currentPage === 1 ? "text-zinc-900" : "black"}
                />
              </button>
              <button
                disabled={currentPage === maxPage}
                className="flex h-8 w-8 items-center justify-center rounded-md text-center text-sm text-zinc-600 hover:bg-[#E67068] hover:text-white disabled:text-neutral-400 disabled:hover:bg-transparent"
              >
                <ChevronRight
                  onClick={handleNextPage}
                  color={currentPage === maxPage ? "text-zinc-900" : "black"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
});

export default TransactionsTableComponent;
